import { useMemo } from "react";
import type { ReactNode } from "react";
import { B2B_USER_TYPE } from "@/types/b2bAccount";
import {
  AnalyticsIdentityContext,
  type AnalyticsIdentity,
} from "./AnalyticsIdentityContext";

interface AnalyticsIdentityProviderProps {
  /** Raw member content-type alias from the layout payload. */
  userType: string | null;
  /**
   * `fullId` from the layout payload — a D2C member's Firebase UID or a B2B
   * member's account key, resolved server-side. Never the login email.
   */
  fullId: string | null;
  /** `b2bUsername` from the layout payload — a B2B member's BC customer number. */
  b2bUsername?: string | null;
  children: ReactNode;
}

export function AnalyticsIdentityProvider({
  userType,
  fullId,
  b2bUsername,
  children,
}: AnalyticsIdentityProviderProps) {
  const identity = useMemo<AnalyticsIdentity>(
    () => ({
      userId: fullId ?? null,
      b2bUsername: b2bUsername ?? null,
      // The layout payload carries the raw Umbraco content-type alias
      // ("member" / "b2bCustomer"), not the 'd2c'/'b2b' shorthand the Remix
      // frontend maps it to. Compare against the same constant the rest of the
      // app uses rather than a literal.
      isCustomerB2b: userType === B2B_USER_TYPE,
    }),
    [fullId, b2bUsername, userType]
  );

  return (
    <AnalyticsIdentityContext.Provider value={identity}>
      {children}
    </AnalyticsIdentityContext.Provider>
  );
}
